import { z } from 'zod';
import {
  SandboxMetricSampleSchema,
  SandboxThresholdsSchema,
  SandboxWarningPayloadSchema,
  SandboxNotifyPayloadSchema,
  SandboxProcessSampleSchema,
} from './sandbox-metrics.types.js';

/**
 * Wire-format version for the bridge control channel. Both sides include
 * `v` on every message so a host running an older bridge (or vice versa)
 * can refuse to talk rather than guess. Mirrored in the Rust crate.
 */
export const SANDBOX_BRIDGE_PROTOCOL_VERSION = 1;

/**
 * Default port the bridge listens on inside the devbox. The server dials
 * this through the existing Runloop open tunnel
 * (`https://{port}-{tunnelKey}.{tunnelDomain}`). Picked at the high end of
 * the registered range to avoid colliding with the agent's own dev servers.
 */
export const SANDBOX_BRIDGE_PORT = 7777;

/**
 * Localhost-only IPC port the bridge listens on for the on-box CLI / daemon
 * skill (the notify path). The `reflex-service` CLI POSTs
 * `{ kind, name, port?, info? }` to `http://127.0.0.1:7778/notify` when it
 * registers a daemon; the bridge relays it over the control WS as a
 * `notify` frame. Bound to loopback only, so no auth beyond being on the box.
 */
export const SANDBOX_BRIDGE_IPC_PORT = 7778;

/**
 * Default detection knobs. Reflex passes effective values to the bridge at
 * launch and echoes the same `thresholds` back to the browser in the
 * `sandbox:metrics` push, so the threshold reference line on the chart
 * always matches what the bridge actually enforces.
 */
export const SANDBOX_BRIDGE_DEFAULT_THRESHOLDS = {
  cpuPct: 85,
  memPct: 85,
  windowSeconds: 60,
} as const;

/** Sample cadence, kept in lockstep with the Rust default. */
export const SANDBOX_BRIDGE_SAMPLE_INTERVAL_MS = 5_000;

/**
 * How much on-box history (seconds) the server asks the bridge to replay
 * when a viewer opens the monitoring panel. The bridge ships its in-memory
 * ring buffer up to this far back before live streaming begins, so reopening
 * the panel restores the rolling window instead of starting empty. The
 * bridge retains a longer window internally (see `HISTORY_RETENTION_MS` in
 * `flex-bridge`) so a full replay is always available. The web client uses
 * the same value to size its rolling chart window.
 */
export const SANDBOX_BRIDGE_REPLAY_SECONDS = 600;

// --- Reflex -> bridge messages ---

export const BridgeMetricsSubscribeSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('metrics.subscribe'),
  /**
   * Optional history replay length (seconds). When set, the bridge ships
   * its in-memory ring buffer up to this many seconds back before live
   * streaming begins. Omit to skip the replay.
   */
  replaySeconds: z.number().positive().optional(),
});

export const BridgeMetricsUnsubscribeSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('metrics.unsubscribe'),
});

export const BridgePingSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('ping'),
});

/**
 * Reflex acks a `sync` notify after it has published the mirrored axon event.
 * The bridge holds each `sync` notify on the box and redelivers it on every
 * reconnect until this ack arrives, then drops it from its queue. `id` is the
 * bridge-assigned `SandboxNotifyPayload.id` being acked. An older bridge that
 * never sent an id simply never receives one of these (and never queued), so
 * the ack is additive and needs no protocol-version bump.
 */
export const BridgeNotifyAckSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('notify.ack'),
  id: z.string(),
});

/**
 * Reflex -> bridge. The result of a relayed {@link BridgeToolCallSchema},
 * correlated by `id`. `ok` distinguishes a tool success (`result` carries the
 * MCP result payload the shim returns to the agent) from a failure (`error`
 * carries a human-readable message the shim surfaces instead). Exactly one of
 * `result` / `error` is meaningful, selected by `ok`. Additive frame — an
 * older bridge that never sends `tool.call` simply never receives one of
 * these, so no protocol-version bump is needed.
 */
export const BridgeToolResultSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('tool.result'),
  id: z.string(),
  ok: z.boolean(),
  result: z.unknown().optional(),
  error: z.string().optional(),
});

export const BridgeServerMessageSchema = z.discriminatedUnion('type', [
  BridgeMetricsSubscribeSchema,
  BridgeMetricsUnsubscribeSchema,
  BridgePingSchema,
  BridgeNotifyAckSchema,
  BridgeToolResultSchema,
]);
export type BridgeServerMessage = z.infer<typeof BridgeServerMessageSchema>;
export type BridgeToolResult = z.infer<typeof BridgeToolResultSchema>;

// --- bridge -> Reflex messages ---

/**
 * First frame the bridge sends on a fresh connection. Carries the
 * box's discovered capacity (CPU cores, memory limit), the threshold
 * values it will use for detection, and a proof of the identity key so
 * Reflex can verify it reached the legit agent's bridge rather than an
 * impostor on that port. The proof is `HMAC(identityKey, helloNonce)`
 * computed by the bridge; Reflex recomputes locally and compares.
 */
export const BridgeHelloSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('hello'),
  cpuCores: z.number().positive(),
  memTotalBytes: z.number().positive(),
  thresholds: SandboxThresholdsSchema,
  helloNonce: z.string(),
  helloProof: z.string(),
  bridgeVersion: z.string(),
});

export const BridgeMetricsSampleSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('metrics.sample'),
  sample: SandboxMetricSampleSchema,
});

export const BridgeWarningSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('warning'),
  warning: SandboxWarningPayloadSchema,
});

/**
 * Periodic `top`-style process snapshot. The bridge samples per-process usage
 * every tick but only streams this frame while a viewer is subscribed (the
 * process viewer panel is open), unlike the always-on detection. Carries a
 * curated top-N list, not every PID, to keep the frame small.
 */
export const BridgeProcessesSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('processes.sample'),
  timestamp: z.number(),
  processes: z.array(SandboxProcessSampleSchema),
});

export const BridgeNotifySchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('notify'),
  notify: SandboxNotifyPayloadSchema,
});

export const BridgePongSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('pong'),
});

/**
 * bridge -> Reflex. The on-box MCP shim received a `tools/call` for a
 * `workstation_*` tool and forwarded it to the bridge's localhost IPC, which
 * relays it here. Reflex executes it with the calling agent's identity: the
 * control socket is per-agent-authenticated, so Reflex resolves which agent
 * (and thus which workstation attachment + access mode) without trusting
 * anything the devbox says. `tool` is the fully-qualified tool name (e.g.
 * `workstation_run_command`); `params` is the opaque MCP arguments object.
 * Correlated to a {@link BridgeToolResultSchema} by `id`. Additive frame.
 */
export const BridgeToolCallSchema = z.object({
  v: z.literal(SANDBOX_BRIDGE_PROTOCOL_VERSION),
  type: z.literal('tool.call'),
  id: z.string(),
  tool: z.string(),
  params: z.unknown(),
});

export const BridgeClientMessageSchema = z.discriminatedUnion('type', [
  BridgeHelloSchema,
  BridgeMetricsSampleSchema,
  BridgeWarningSchema,
  BridgeProcessesSchema,
  BridgeNotifySchema,
  BridgePongSchema,
  BridgeToolCallSchema,
]);
export type BridgeClientMessage = z.infer<typeof BridgeClientMessageSchema>;
export type BridgeToolCall = z.infer<typeof BridgeToolCallSchema>;

export type BridgeHelloMessage = z.infer<typeof BridgeHelloSchema>;
